import { request } from "../api/http"

export interface LoginInput {
  org: string
  staffCode: string
  password: string
  /** Staff who work at a station pick it at login; the server enforces one staff ↔ one station. */
  stationCode?: string
}

export interface TokenResponse {
  accessToken: string
  /** Lifetime of the access token in seconds (server default: 900). */
  expiresIn: number
}

type Listener = (authenticated: boolean) => void

/**
 * Access token lives only in memory (never localStorage). The long-lived
 * refresh token is an httpOnly cookie the browser sends to /api/v1/auth/*.
 * The server can revoke it at any time (logout, deactivation, force-offline).
 */
function createSession() {
  let accessToken: string | null = null
  let refreshTimer: ReturnType<typeof setTimeout> | undefined
  let inflightRefresh: Promise<string | null> | null = null
  const listeners = new Set<Listener>()

  function setToken(token: TokenResponse | null) {
    clearTimeout(refreshTimer)
    const wasAuthenticated = accessToken !== null
    accessToken = token?.accessToken ?? null

    if (token) {
      // Refresh a minute before expiry so requests rarely hit a 401.
      const delayMs = Math.max(5, token.expiresIn - 60) * 1000
      refreshTimer = setTimeout(() => void refresh(), delayMs)
    }
    if (wasAuthenticated !== (accessToken !== null)) {
      for (const listener of listeners) listener(accessToken !== null)
    }
  }

  /** Single-flight: concurrent 401s share one refresh request. Resolves to null when the session is gone. */
  function refresh(): Promise<string | null> {
    inflightRefresh ??= request<TokenResponse>("/auth/refresh", { method: "POST" })
      .then((token) => {
        setToken(token)
        return token.accessToken
      })
      .catch(() => {
        setToken(null)
        return null
      })
      .finally(() => {
        inflightRefresh = null
      })
    return inflightRefresh
  }

  return {
    getAccessToken: () => accessToken,
    isAuthenticated: () => accessToken !== null,

    subscribe(listener: Listener): () => void {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    async login(input: LoginInput): Promise<void> {
      setToken(await request<TokenResponse>("/auth/login", { method: "POST", body: input }))
    },

    refresh,

    /** True when a token is in memory or can be obtained from the refresh cookie (e.g. after a page reload). */
    async ensure(): Promise<boolean> {
      return accessToken !== null || (await refresh()) !== null
    },

    async logout(): Promise<void> {
      try {
        await request<void>("/auth/logout", { method: "POST", token: accessToken })
      } finally {
        setToken(null)
      }
    },

    /** Test helper — resets module state between tests. */
    reset(): void {
      clearTimeout(refreshTimer)
      accessToken = null
      inflightRefresh = null
      listeners.clear()
    },
  }
}

export const session = createSession()
